import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { storage } from '../config/firebase'
import {
  MAX_DESIGN_UPLOAD_BYTES,
  MAX_PRINT_UPLOAD_BYTES,
  assertAllowedImageBlob
} from './imageValidation'
import { findInlineImages, hostInlineImages, persistableImageUrl } from '../utils/designImageUrl'

function parseDataUrl(input) {
  const match = input.match(/^data:([^;]+);base64,(.+)$/)
  if (!match) throw new Error('Invalid image data. Expected a base64 image.')
  return { mime: match[1], base64: match[2] }
}

function base64ToBlob(base64, mime) {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i)
  }
  return new Blob([bytes], { type: mime || 'application/octet-stream' })
}

async function inputToValidatedBlob(input, maxBytes) {
  if (typeof input !== 'string' || !input.trim()) {
    throw new Error('No image provided for upload')
  }

  let blob
  if (input.startsWith('data:')) {
    const { mime, base64 } = parseDataUrl(input)
    blob = base64ToBlob(base64, mime)
  } else if (input.startsWith('http://') || input.startsWith('https://')) {
    const response = await fetch(input)
    if (!response.ok) {
      throw new Error(`Could not fetch image URL (${response.status})`)
    }
    blob = await response.blob()
  } else {
    throw new Error('Image must be a data URL or https URL')
  }

  const checked = await assertAllowedImageBlob(blob, maxBytes)
  return {
    blob: new Blob([checked.bytes], { type: checked.mime }),
    mime: checked.mime,
    ext: checked.ext
  }
}

/** The URL to save for a design image: our hosted file, never base64. */
export function persistableDesignImage(input, uid) {
  return persistableImageUrl(input, {
    upload: (value) => uploadDesignImage(value, uid),
    bucketName: storage?.app?.options?.storageBucket
  })
}

/**
 * A design (or design update) ready for the user document: every base64 image
 * in it is uploaded to Storage and replaced with its URL. Throws rather than
 * return anything that still holds base64.
 */
export async function hostDesignImages(design, uid) {
  const hosted = await hostInlineImages(design, (dataUrl) => uploadDesignImage(dataUrl, uid))
  const left = findInlineImages(hosted)
  if (left.length) {
    console.warn('[designStorage] base64 left after upload:', left)
    throw new Error("We couldn't upload your design images. Please try again.")
  }
  return hosted
}

/**
 * Upload a generated design image to Firebase Storage.
 * @param {string} input - data:image/...;base64,... or https image URL
 * @param {string} uid - Firebase user id
 * @returns {Promise<string>} permanent download URL
 */
export async function uploadDesignImage(input, uid) {
  if (!uid) throw new Error('User id is required to upload a design image')
  if (!storage) throw new Error('Firebase Storage is not initialized')

  const { blob, mime, ext } = await inputToValidatedBlob(input, MAX_DESIGN_UPLOAD_BYTES)
  const timestamp = Date.now()
  const path = `designs/${uid}/${timestamp}.${ext}`
  const storageRef = ref(storage, path)

  await uploadBytes(storageRef, blob, { contentType: mime })
  return getDownloadURL(storageRef)
}

/**
 * Upload a Gelato print-ready image (artwork only). Uses a download-token URL
 * from getDownloadURL — publicly fetchable by Gelato with the token.
 *
 * @param {string} input - data URL or https URL
 * @param {string} uid
 * @param {{ orderId?: string, view?: string }} [opts]
 */
export async function uploadPrintFile(input, uid, opts = {}) {
  if (!uid) throw new Error('User id is required to upload a print file')
  if (!storage) throw new Error('Firebase Storage is not initialized')

  const { blob, mime, ext } = await inputToValidatedBlob(input, MAX_PRINT_UPLOAD_BYTES)
  const view = opts.view || 'front'
  const orderPart = opts.orderId ? `${opts.orderId}-` : ''
  const path = `designs/${uid}/print/${orderPart}${view}-${Date.now()}.${ext}`
  const storageRef = ref(storage, path)

  await uploadBytes(storageRef, blob, { contentType: mime })
  return getDownloadURL(storageRef)
}
